<template>
  <ui-menu-group>
    <template v-if="name" #name>
      {{ name }}
    </template>

    <!-- ! the :key attribute has both id && label, because some dropdowns might weirdly have duplicate ids (ScorePickerV2)

      Edit: Adding index to key, because currently it's the only unique option identifier. 
      Without it, Vue will throw a warning about duplicate keys and custom category search does not work properly.
      Remove index from key, once BE has resolved duplicate ids issue in custom Categories -->
    <ui-dropdown-item
      v-for="(option, index) in options"
      :key="`${option[props.propToCheck]}-${option?.id || ''}${index}`"
      :option="option"
      :selected="getIsSelected(option)"
      :indeterminate="getIsIndeterminate(option)"
      :mode="props.mode"
      :label="props.label"
      :disabled="isDisabled(option)"
      :separator="isSeparator(option)"
      :prop-to-check="props.propToCheck"
      @toggle="(option) => $emit('toggle', option)"
    >
      <template
        v-if="$slots['option-icon'] && option[props.propToCheck] !== ALL_ID"
        #option-icon="{ option: slotOption }"
      >
        <slot name="option-icon" :option="slotOption" />
      </template>
      <template #option="{ option: slotOption }">
        <slot name="option" :option="slotOption" />
      </template>
    </ui-dropdown-item>

    <template v-if="emptyText" #empty>
      {{ emptyText }}
    </template>

    <template v-if="total && total > optionsLength" #more>
      <slot name="more-count" :count="total - optionsLength" />
    </template>
  </ui-menu-group>
</template>

<script lang="ts" setup generic="T extends object, P extends keyof T, L extends keyof T, M extends Modes">
import { computed } from 'vue'

import UiMenuGroup from '../UiMenu/UiMenuGroup.vue'

import { type Modes } from './types'
import UiDropdownItem from './UiDropdownItem.vue'
import { ALL_ID } from './constants'

const props = defineProps<{
  options: (T & {
    id?: string
  })[]
  name?: string
  mode: M
  label: L
  listRef?: HTMLDivElement
  total?: number
  emptyText?: string
  propToCheck: P
  getIsSelected: (option: T) => boolean
  getIsIndeterminate: (option: T) => boolean
}>()

defineEmits<{
  toggle: [item: any]
}>()

const isOptionPropInList = (option: T, prop: string, list: string[]) =>
  prop in option && typeof option[prop] === 'string' && list.includes(option[prop])

const isDisabled = (option: T) => {
  const markedAsDisabled = ['separator', 'placeholder']
  return isOptionPropInList(option, 'name', markedAsDisabled) || isOptionPropInList(option, 'value', markedAsDisabled)
}

const isSeparator = (option: T) =>
  isOptionPropInList(option, 'name', ['separator']) || isOptionPropInList(option, 'value', ['separator'])

const optionsLength = computed(() => {
  return props.options.filter((opt) => !isSeparator(opt)).length
})

defineExpose({ optionsLength })
</script>
